
import { useEffect, useState } from 'react';
import { Modal as AntdModal, Button } from 'antd';

import ScriptView from '../../script/ScriptView';
import { readClipboard, writeClipboard } from './clipboard';

const EVENT_LABELS = ['On Start', 'On Change', 'On Save'];

// Recursively counts script nodes referencing a variable by name (nodes carry
// the reference in their `var` field — see blockRegistry var-select fields).
function countVarInNode(node, name) {
    if (!node || typeof node !== 'object') return 0;
    let count = node.var === name ? 1 : 0;
    if (Array.isArray(node.children)) {
        for (const child of node.children) {
            count += countVarInNode(child, name);
        }
    }
    return count;
}

export default function ViewVars({ comps, setComps, setView, vars, setVars,
    externals, setExternals, page, setPage, setSelectedEl, creationIndex, setCreationIndex }) {

    const [dragIndex, setDragIndex] = useState(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);
    // Index of the variable whose "where is this used?" panel is expanded.
    const [usagesFor, setUsagesFor] = useState(null);
    // Usages of that variable — computed on demand when the locate icon is
    // clicked (scanning every script tree for every variable on each render
    // would get expensive on large forms).
    const [usageList, setUsageList] = useState(null);
    // { id, tab } of the script site being visited via the usage panel.
    const [jump, setJump] = useState(null);
    // Indices of variables ticked for copy. Cleared on any list mutation,
    // since indices shift under reorder/delete/duplicate/paste.
    const [selected, setSelected] = useState(() => new Set());
    // Anchor for shift-click range selection: the last individually clicked row.
    const [lastToggled, setLastToggled] = useState(null);
    const [clipboardCount, setClipboardCount] = useState(() => readClipboard('vars').length);

    // Refresh the paste count when another tab copies (same-tab copies update
    // it directly in copySelected).
    useEffect(() => {
        const onStorage = () => setClipboardCount(readClipboard('vars').length);
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, []);

    function toggleSelected(k, shiftKey) {
        const next = new Set(selected);
        if (shiftKey && lastToggled !== null && lastToggled !== k) {
            // Extend the anchor's current state (selected or not) over the range.
            const apply = selected.has(lastToggled);
            const lo = Math.min(lastToggled, k);
            const hi = Math.max(lastToggled, k);
            for (let i = lo; i <= hi; i++) {
                if (apply) next.add(i); else next.delete(i);
            }
        } else {
            if (next.has(k)) next.delete(k); else next.add(k);
        }
        setLastToggled(k);
        setSelected(next);
    }

    function clearSelection() {
        setSelected(new Set());
        setLastToggled(null);
    }

    function copySelected() {
        const items = vars.filter((_, i) => selected.has(i));
        writeClipboard('vars', items);
        setClipboardCount(items.length);
        clearSelection();
    }

    function pasteVars() {
        const items = readClipboard('vars').filter((item) =>
            item && typeof item.name === 'string' && typeof item.type === 'string');
        if (items.length === 0) return;
        const names = new Set(vars.map((v) => v.name));
        const pasted = items.map((item) => {
            let name = item.name;
            if (names.has(name)) {
                const parts = splitStringAndNumber(name);
                const stem = parts.length > 0 ? parts[0] : name;
                let n = parts.length > 0 ? parts[1] + 1 : 2;
                while (names.has(stem + n)) n++;
                name = stem + n;
            }
            names.add(name);
            return { ...item, name };
        });
        setUsagesFor(null);
        setUsageList(null);
        clearSelection();
        setVars([...vars, ...pasted]);
    }

    function findUsages(name) {
        const usages = [];
        for (let c = 0; c < comps.length; c++) {
            const script = comps[c].script;
            if (!Array.isArray(script)) continue;
            const events = [];
            for (let ev = 0; ev < script.length; ev++) {
                const count = countVarInNode(script[ev], name);
                if (count > 0) events.push({ event: ev, count });
            }
            if (events.length > 0) usages.push({ comp: c, events });
        }
        return usages;
    }

    // Identifying text of a component (its value/placeholder), if it has any.
    function compText(comp) {
        if (typeof comp?.props?.value === 'string' && comp.props.value.trim() !== '') {
            return comp.props.value.trim();
        }
        if (typeof comp?.props?.placeholder === 'string' && comp.props.placeholder.trim() !== '') {
            return comp.props.placeholder.trim();
        }
        return '';
    }

    function hopTo(compIdx, eventIdx) {
        if (setPage && comps[compIdx].page !== page) setPage(comps[compIdx].page);
        if (setSelectedEl) setSelectedEl(compIdx);
        setJump({ id: compIdx, tab: eventIdx });
    }

    function moveVar(from, to) {
        if (from === null || to === null || from === to) return;
        let new_vars = [...vars];
        const [moved] = new_vars.splice(from, 1);
        new_vars.splice(to, 0, moved);
        setUsagesFor(null);
        setUsageList(null);
        clearSelection();
        setVars(new_vars);
    }

    // Discrete per-type styling: small icon badge + a light row tint.
    const typeMeta = {
        Number: { icon: 'fa-hashtag', fg: '#3b6fd4', bg: '#f1f6ff', border: '#dbe6fb' },
        String: { icon: 'fa-font', fg: '#3f9a5e', bg: '#f1f8f3', border: '#d8ecdd' },
        Color: { icon: 'fa-tint', fg: '#8257c4', bg: '#f6f1fc', border: '#e6dcf5' },
        Boolean: { icon: 'fa-toggle-on', fg: '#bf8636', bg: '#fcf6ea', border: '#f0e5cc' }
    };

    function splitStringAndNumber(value) {
        const match = value.match(/^([a-zA-Z]+)(\d+)$/);
        if (match) {
            return [match[1], parseInt(match[2], 10)];
        } else {
            return []; // or undefined, or [] depending on your preference
        }
    }

    let vars_list = [];
    for (let k = 0; k < vars.length; k++) {
        const meta = typeMeta[vars[k].type] || typeMeta.Number;
        vars_list.push(
            <div
                onDragOver={(e) => {
                    if (dragIndex === null) return;
                    e.preventDefault();
                    if (dragOverIndex !== k) setDragOverIndex(k);
                }}
                onDrop={(e) => {
                    e.preventDefault();
                    moveVar(dragIndex, k);
                    setDragIndex(null);
                    setDragOverIndex(null);
                }}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.6vmax',
                    padding: '0.5vmax',
                    textAlign: 'justify',
                    border: '1px solid ' + meta.border,
                    borderTop: dragOverIndex === k && dragIndex !== null && dragIndex !== k
                        ? '2px solid var(--ed-accent)' : '1px solid ' + meta.border,
                    borderRadius: 6,
                    marginBottom: 8,
                    backgroundColor: meta.bg,
                    opacity: dragIndex === k ? 0.4 : 1,
                    transition: 'opacity .1s, border-color .1s'
                }}>
                <input type='checkbox' title="Select for copy (shift-click to select a range)"
                    checked={selected.has(k)}
                    onClick={(e) => toggleSelected(k, e.shiftKey)}
                    onChange={() => { }}
                    style={{
                        flex: '0 0 auto',
                        width: '1vmax',
                        height: '1vmax',
                        accentColor: 'var(--ed-accent)',
                        cursor: 'pointer'
                    }} />
                <span style={{
                    flex: '0 0 auto',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '2vmax',
                    height: '2vmax',
                    borderRadius: 6,
                    color: meta.fg,
                    backgroundColor: '#fff',
                    border: '1px solid ' + meta.border,
                    fontSize: '1vmax'
                }} title={vars[k].type}>
                    <i className={'fa fa-solid ' + meta.icon}></i>
                </span>
                <div style={{
                    flex: '1 1 auto',
                    minWidth: 0
                }}>
                    <select className='panel-dropdown' style={{
                        fontSize: '1vmax',
                        width: '32.5%'
                    }} onChange={(e) => {
                        let new_vars = [...vars];
                        new_vars[k].type = e.target.value;
                        if (e.target.value === 'Color') {
                            new_vars[k].value = '#ffffff';
                        } else if (e.target.value === 'Number') {
                            new_vars[k].value = '0';
                        } else if (e.target.value === 'String') {
                            new_vars[k].value = 'A';
                        } else if (e.target.value === 'Boolean') {
                            new_vars[k].value = 'false';
                        }
                        setVars(new_vars);
                    }}
                        value={vars[k].type}>
                        <option value='Number'>Number</option>
                        <option value='String'>String</option>
                        <option value='Boolean'>Boolean</option>
                        <option value='Color'>Color</option>
                    </select>
                    <input className='panel-input' style={{
                        marginLeft: '0.75vmax',
                        fontSize: '1vmax',
                        width: '20%'
                    }} value={vars[k].name} onChange={(e) => {
                        let new_vars = [...vars];
                        new_vars[k].name = e.target.value;
                        // Usages are matched by name, so a rename invalidates
                        // this variable's open usage panel — collapse it.
                        if (usagesFor === k) {
                            setUsagesFor(null);
                            setUsageList(null);
                        }
                        setVars(new_vars);
                    }} />
                    <p className='panel-label' style={{
                        marginLeft: '0.5vmax',
                        fontSize: '1.25vmax'
                    }}>=</p>
                    {vars[k].type === 'Number' ||
                        vars[k].type === 'String' ? <input className='panel-input' style={{
                            fontSize: '1vmax',
                            width: '20%'
                        }} value={vars[k].value} onChange={(e) => {
                            let new_vars = [...vars];
                            new_vars[k].value = e.target.value;
                            setVars(new_vars);
                        }} /> : <></>}
                    {
                        vars[k].type === 'Color' ?
                            <input type='color' className='panel-color-input' onChange={(e) => {
                                let new_vars = [...vars];
                                new_vars[k].value = e.target.value;
                                setVars(new_vars);
                            }} value={vars[k].value} />
                            : <></>
                    }
                    {
                        vars[k].type === 'Boolean' ?
                            <select className='panel-dropdown' style={{
                                fontSize: '1vmax',
                                width: '22.5%'
                            }} onChange={(e) => {
                                let new_vars = [...vars];
                                new_vars[k].value = e.target.value;
                                setVars(new_vars);
                            }} value={vars[k].value}>
                                <option value='true'>True</option>
                                <option value='false'>False</option>
                            </select> : <></>
                    }
                </div>
                <div style={{
                    flex: '0 0 auto',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    gap: '1vmax'
                }}>
                    <span
                        draggable
                        onDragStart={() => setDragIndex(k)}
                        onDragEnd={() => {
                            setDragIndex(null);
                            setDragOverIndex(null);
                        }}
                        title="Drag to reorder"
                        className="var-drag-handle"
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.4vmax',
                            padding: '0.35vmax 0.75vmax',
                            border: '1px dashed var(--ed-border-strong)',
                            borderRadius: 6,
                            color: 'var(--ed-text-soft)',
                            fontSize: '1vmax',
                            cursor: 'grab',
                            userSelect: 'none'
                        }}>
                        <i className="fa fa-solid fa-arrows-v"></i>
                        Drag
                    </span>
                    <i className="fa fa-solid fa-crosshairs pointy" title="Locate usages in scripts"
                        style={{
                            color: usagesFor === k ? 'var(--ed-accent)' : 'var(--ed-text-soft)',
                            fontSize: '1.25vmax'
                        }} onClick={() => {
                            if (usagesFor === k) {
                                setUsagesFor(null);
                                setUsageList(null);
                            } else {
                                setUsagesFor(k);
                                setUsageList(findUsages(vars[k].name));
                            }
                        }}></i>
                    <i className="fa fa-solid fa-clone pointy" title="Duplicate" style={{
                        color: 'var(--ed-text-soft)',
                        fontSize: '1.25vmax'
                    }} onClick={() => {
                        const names = new Set(vars.map((v) => v.name));
                        const parts = splitStringAndNumber(vars[k].name);
                        const stem = parts.length > 0 ? parts[0] : vars[k].name;
                        let n = parts.length > 0 ? parts[1] + 1 : 2;
                        while (names.has(stem + n)) n++;
                        let new_vars = [...vars];
                        new_vars.splice(k + 1, 0, {
                            ...vars[k],
                            name: stem + n
                        });
                        setUsagesFor(null);
                        setUsageList(null);
                        clearSelection();
                        setVars(new_vars);
                    }}></i>
                    <i className="fa fa-solid fa-trash pointy" style={{
                        color: 'var(--ed-text-soft)',
                        fontSize: '1.25vmax'
                    }} onClick={() => {
                        let new_vars = [...vars];
                        new_vars.splice(k, 1);
                        setUsagesFor(null);
                        setUsageList(null);
                        clearSelection();
                        setVars(new_vars);
                    }}></i>
                </div>
            </div>
        );
        if (usagesFor === k) {
            const totalRefs = (usageList || []).reduce(
                (sum, u) => sum + u.events.reduce((s, e) => s + e.count, 0), 0);
            vars_list.push(
                <div style={{
                    margin: '-4px 0 8px',
                    padding: '0.5vmax 0.75vmax',
                    border: '1px solid ' + meta.border,
                    borderTop: 'none',
                    borderRadius: '0 0 6px 6px',
                    backgroundColor: '#fff',
                    fontSize: '1vmax'
                }}>
                    {(!usageList || usageList.length === 0) ?
                        <div style={{ color: 'var(--ed-text-soft)', padding: '0.35vmax 0' }}>
                            Not used in any component scripts.
                        </div> :
                        <>
                            <div style={{
                                color: 'var(--ed-text-soft)',
                                fontSize: '0.85vmax',
                                fontWeight: 600,
                                textTransform: 'uppercase',
                                letterSpacing: '.04em',
                                padding: '0.15vmax 0 0.35vmax',
                                borderBottom: '1px solid var(--ed-border)',
                                marginBottom: '0.25vmax'
                            }}>
                                {totalRefs} reference{totalRefs === 1 ? '' : 's'} in {usageList.length} component{usageList.length === 1 ? '' : 's'}
                            </div>
                            <div style={{ maxHeight: '22vh', overflowY: 'auto' }}>
                                {usageList.map((u) => (
                                    <div key={u.comp} style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.6vmax',
                                        padding: '0.35vmax 0',
                                        borderBottom: '1px solid var(--ed-border)'
                                    }}>
                                        <span style={{
                                            flex: '0 0 auto',
                                            padding: '0.1vmax 0.5vmax',
                                            borderRadius: 4,
                                            border: '1px solid var(--ed-border)',
                                            backgroundColor: 'var(--ed-surface-2)',
                                            color: 'var(--ed-text)',
                                            fontSize: '0.8vmax',
                                            fontWeight: 700,
                                            textTransform: 'uppercase',
                                            letterSpacing: '.03em'
                                        }}>{comps[u.comp].name}</span>
                                        <span style={{
                                            flex: '0 0 auto',
                                            padding: '0.1vmax 0.5vmax',
                                            borderRadius: 4,
                                            border: '1px solid ' + meta.border,
                                            backgroundColor: meta.bg,
                                            color: meta.fg,
                                            fontSize: '0.8vmax',
                                            fontWeight: 700
                                        }}>Page {comps[u.comp].page + 1}</span>
                                        <span style={{
                                            flex: '1 1 auto',
                                            minWidth: 0,
                                            overflowX: 'auto',
                                            whiteSpace: 'nowrap',
                                            color: 'var(--ed-text)'
                                        }}>
                                            {compText(comps[u.comp]) || <span style={{
                                                color: 'var(--ed-text-soft)',
                                                fontStyle: 'italic'
                                            }}>(no text)</span>}
                                        </span>
                                        <span style={{ flex: '0 0 auto', display: 'inline-flex', gap: '0.4vmax' }}>
                                            {u.events.map((e) => (
                                                <a key={e.event} className='panel-button'
                                                    title={'Open this script at ' + EVENT_LABELS[e.event]}
                                                    onClick={(ev) => {
                                                        ev.preventDefault();
                                                        hopTo(u.comp, e.event);
                                                    }}>
                                                    <i className="fa fa-solid fa-external-link" style={{ marginRight: 4 }}></i>
                                                    {EVENT_LABELS[e.event]}{e.count > 1 ? ' ×' + e.count : ''}
                                                </a>
                                            ))}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </>}
                </div>
            );
        }
    }

    return (
        <>
            {/* <Modal width={30} height={40}>
            <h1 style={{
                color: '#FAF3DD',
                fontWeight: '300',
                margin: '0',
                padding: '1vmax 0',
                fontSize: '1.75vmax',
                borderBottom: '1px #FAF3DD solid',
                display: 'block'
                }}>View Variables</h1> */}

            <AntdModal className="cf-modal" title="View Variables" width="min(950px, 92vw)" visible={jump === null} onOk={() => {
                setView('');
            }} onCancel={() => {
                setView('');
            }}
                footer={[
                    <Button key="copy" disabled={selected.size === 0}
                        title="Copy selected variables (paste here or in another form)"
                        onClick={(e) => {
                            e.preventDefault();
                            copySelected();
                        }}>
                        Copy{selected.size > 0 ? ' (' + selected.size + ')' : ''}
                    </Button>,
                    <Button key="paste" disabled={clipboardCount === 0}
                        title="Paste copied variables at the end of the list"
                        onClick={(e) => {
                            e.preventDefault();
                            pasteVars();
                        }}>
                        Paste{clipboardCount > 0 ? ' (' + clipboardCount + ')' : ''}
                    </Button>,
                    <Button key="add" type="primary" onClick={(e) => {
                        e.preventDefault();

                        if (vars.length > 0) {
                            if (splitStringAndNumber(vars[vars.length - 1].name).length > 0) {
                                let new_vars = [...vars];
                                new_vars.push({
                                    name: splitStringAndNumber(vars[vars.length - 1].name)[0] + (splitStringAndNumber(vars[vars.length - 1].name)[1] + 1).toString(),
                                    type: vars[vars.length - 1].type,
                                    value: vars[vars.length - 1].value
                                });
                                setVars(new_vars);
                            } else {
                                let new_vars = [...vars];
                                new_vars.push({
                                    name: 'New',
                                    type: vars[vars.length - 1].type,
                                    value: vars[vars.length - 1].value
                                });

                                setVars(new_vars);
                            }
                        } else {
                            let new_vars = [...vars];
                            new_vars.push({
                                name: 'New',
                                type: 'Number',
                                value: '1'
                            });

                            setVars(new_vars);
                        }
                    }}>
                        Add Variable
                    </Button>,
                    <Button key="close" type="primary" danger onClick={(e) => { setView(''); }}>
                        Close
                    </Button>
                ]}
            >
                {vars.length > 0 ?
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5vmax',
                        marginBottom: 8,
                        fontSize: '0.9vmax',
                        color: 'var(--ed-text-soft)'
                    }}>
                        <input type='checkbox'
                            checked={selected.size === vars.length}
                            onChange={() => {
                                setSelected(selected.size === vars.length
                                    ? new Set() : new Set(vars.map((_, i) => i)));
                            }}
                            style={{
                                width: '1vmax',
                                height: '1vmax',
                                accentColor: 'var(--ed-accent)',
                                cursor: 'pointer'
                            }} />
                        Select all
                        {selected.size > 0 ? <span>— {selected.size} selected</span> : <></>}
                    </div> : <></>}
                <div style={{
                    maxHeight: '55vh',
                    overflow: 'scroll'
                }}>
                    {vars_list}
                </div>
                {/* <div style={{
                position: 'absolute',
                bottom: '0',
                width: '100%',
                backgroundColor: '#000000'
            }}>
                <a href='' className='panel-button' style={{
                    margin: '1vmax'
                }} onClick={(e) => {
                    e.preventDefault();
                    let new_vars = [...vars];
                    new_vars.push({
                        name: 'New',
                        type: 'Number',
                        value: '1'
                    }); setVars(new_vars);
                }}>Add Variable</a>
                <a href='' className='panel-button' style={{
                    marginBottom: '1vmax'
                }} onClick={(e) => {
                    e.preventDefault();
                    setView('');
                }}>Ok</a>
                </div> */}
            </AntdModal>
            {/* </Modal> */}

            {jump !== null ? <ScriptView id={jump.id} comps={comps} setComps={setComps}
                setIsShowingScript={(v) => {
                    if (!v) {
                        setJump(null);
                        // The visited script may have gained/lost references —
                        // refresh the open usage panel so it stays accurate.
                        if (usagesFor !== null && vars[usagesFor]) {
                            setUsageList(findUsages(vars[usagesFor].name));
                        }
                    }
                }}
                vars={vars} setVars={setVars} externals={externals} setExternals={setExternals}
                creationIndex={creationIndex} setCreationIndex={setCreationIndex}
                initialTab={jump.tab} /> : <></>}
        </>
    );

}